// Phase D's story stream (gate D1.3, quests, dialogue and the town): the story layer in the default scene (phase D
// joined; `?scene=story` names the same world). It joins the valley's one kernel game through the world scene's addons
// (src/world/scene.ts): the story module (module.ts: @rpg/story and @rpg/dialogue's ink conversations) over the town's
// townsfolk (their places, names and greetings; a press on one is the town's `npc:interact`), the town's cutscenes
// (`story:scene`, answered with `story:scene-done`), the rpg layer's items, gold and shops, and one condition language
// the rpg layer and the town register their facts on. The handle's keys are under `__rpg.story`; the dialogue box and
// the quest log are ui.ts; the play-through bot is bot.ts.
// Planted defect (`?faults=ink-line-no-id`): the next ink line read loses its string id; the story's invariant reports
// it (the conversation ends).
import type { Invariant } from "@rpg/harness";
import type { EventBus } from "@rpg/kernel";
import type { Runtime } from "@rpg/runtime";
import { INK_FAULTS } from "@rpg/dialogue";
import type { SpikeContent } from "../content";
import type { GameEvents } from "../core";
import type { FaultGroup } from "../faults";
import type { WorldMount } from "../world/scene";
import { teleportHero } from "../world/teleport";
import type { StoryLayer, StoryNpcs } from "./module";
import type { storyScene } from "./scene";
import { storyBot } from "./bot";

export { mountStoryUi } from "./ui";

/** The story's planted defects, as `?faults=` and `__rpg.faults()` take them. */
export const STORY_FAULT_NAMES = ["ink-line-no-id"] as const;

let lineNoId = false;
/** The group for src/faults.ts's joined record (the switch lives beside what it breaks: @rpg/dialogue's ink runner). */
export const storyFaults: FaultGroup = {
  names: STORY_FAULT_NAMES,
  get: () => lineNoId,
  set: (_n, on) => {
    lineNoId = on;
    INK_FAULTS.dropLineId = on;
  },
};

/** Whether a page runs the story scene. */
export const isStoryScene = (search: string): boolean => new URLSearchParams(search).get("scene") === "story";

/** One kernel event the story stream sends or hears, as logged for the handle. */
export interface StoryEventLog {
  step: number;
  name: string;
  detail: Record<string, unknown>;
}

const LOGGED = ["story:quest", "story:scene", "story:scene-done", "dialogue:open", "dialogue:line", "dialogue:close"] as const;

/** The cutscenes as the story's handle and bot read them (the town's). */
export interface StoryScenes {
  /** The scene playing (or queued), none when empty. */
  playing(): string[];
  played(): string[];
  skip(): void;
}

/** The story's planted defects from the page's `?faults=` (set before the first line is read). */
export function applyStoryFaultSwitches(search: string): void {
  if ((new URLSearchParams(search).get("faults") ?? "").split(",").includes("ink-line-no-id")) storyFaults.set("ink-line-no-id", true);
}

/** The story's handle and the UI's reads over the joined world's story (src/joined.ts builds it, the town's townsfolk
 *  and cutscenes under it); `attach` once the sim exists. */
export function storySceneFor(o: { content: SpikeContent; story: ReturnType<typeof storyScene>; npcs: StoryNpcs; scenes: StoryScenes }) {
  const sc = o.story;
  const { addon } = sc;
  const scenes = o.scenes;
  let mount: WorldMount | null = null;
  let events: EventBus<GameEvents> | null = null;
  let dispatch: ((action: string, payload?: unknown) => void) | null = null;
  let runtime: Runtime | null = null;
  const log: StoryEventLog[] = [];
  let step = 0;
  const need = (): StoryLayer => sc.layer();
  const layerOrNull = (): StoryLayer | null => {
    try {
      return sc.layer();
    } catch {
      return null;
    }
  };
  const npcName = (id: string): string => (id === "hero" ? "character.hero" : o.npcs.ids().includes(id) ? o.npcs.name(id) : `character.${id}`);
  /** What the dialogue box shows now (string ids), or null. */
  const view = () => {
    const l = need();
    const s = l.conversations.state();
    if (!s.npc || !s.beat || s.beat.kind === "end") return null;
    const speaker = s.beat.kind === "line" ? (s.speaker ?? s.npc) : s.npc;
    return {
      npc: s.npc,
      speaker,
      name: npcName(speaker),
      waiting: s.waiting.length > 0,
      line: s.beat.kind === "line" ? s.beat.id : null,
      choices: s.beat.kind === "choices" ? s.beat.choices.map((c) => c.id) : [],
      lines: s.lines,
    };
  };
  const objective = () => {
    const ob = need().objective();
    return ob ? { ...ob, text: o.content.t(ob.label) } : null;
  };
  const summary = () => {
    const l = need();
    const st = l.story;
    return {
      quests: Object.fromEntries(st.quests.list().map((q) => [q.id, st.status(q.id)])),
      objective: st.objective(),
      affinity: Object.fromEntries(st.affinity.npcs().map((n) => [n, st.affinity.get(n)])),
      standing: Object.fromEntries(st.standing.factions().map((f) => [f, { score: st.standing.get(f), tier: st.standing.tier(f).id, discount: st.conditions.fact(`standing.${f}.discount`) }])),
      talking: l.conversations.state().npc,
      scenes: scenes.playing(),
      layer: l.state(),
    };
  };
  const self = {
    addon,
    /** The quest journal's sim side (its module and change listeners). */
    journal: sc.journal,
    layer: need,
    view,
    objective,
    summary,
    npcName,
    /** Once the sim exists: its dispatch and bus (the events log, the bot), the world mount (teleports). */
    attach(a: { dispatch: (action: string, payload?: unknown) => void; events: EventBus<GameEvents>; runtime: Runtime; world: WorldMount }): void {
      dispatch = a.dispatch;
      events = a.events;
      runtime = a.runtime;
      mount = a.world;
      a.events.on("core:step", (e) => void (step = e.step));
      for (const name of LOGGED)
        a.events.on(name, (p: unknown) => {
          log.push({ step, name, detail: { ...(p as Record<string, unknown>) } });
          if (log.length > 400) log.splice(0, log.length - 300);
        });
    },
    invariants: (): Invariant[] => [() => layerOrNull()?.problems()[0] ?? null],
    events: (): EventBus<GameEvents> | null => events,
    handle: {
      /** The story now: quests' states, the marker's objective, affinity, standing (with its discount), who talks. */
      state: () => summary(),
      /** The quest log's entries. */
      journal: () => need().story.journal(),
      /** The objective marker (seam 5, the HUD's `objective()`): its label's words, the string id and where. */
      objective,
      /** A fact's value, and a condition's truth, through the shared conditions. */
      fact: (name: string) => need().story.conditions.fact(name),
      test: (cond: string) => need().story.conditions.test(cond),
      /** The event log, newest first. */
      deeds: (limit = 20) => need().story.log.query({ limit }),
      /** What a townsperson remembers. */
      memory: (npc: string) => need().story.memory.entries(npc),
      /** The dialogue box's view; talk (as a press on them would), advance, choose; track a quest. */
      dialogue: () => view(),
      talk: (npc: string) => dispatch?.("story/talk", { npc }),
      advance: () => dispatch?.("story/advance"),
      choose: (n: number) => dispatch?.("story/choose", { n }),
      track: (quest: string | null) => dispatch?.("story/track", { quest }),
      /** A new game (the title's first play): the opening's trigger waits for it; `false` starts after the opening. */
      newGame: (opening = true) => dispatch?.("story/new-game", { opening }),
      /** Skip the scene playing (the town's cutscene), and the scenes played. */
      skipScene: () => scenes.skip(),
      scenes: () => ({ playing: scenes.playing(), played: scenes.played() }),
      /** The barks said (not saved), the story and dialogue events on the kernel bus. */
      barks: () => need().said(),
      events: (name?: string) => log.filter((e) => !name || e.name === name).map((e) => ({ ...e })),
      /** Warp the hero `dist` m in front of a townsperson, facing them (the sim's camera yaw held). */
      approach: (npc: string, dist = 1.4) => {
        const at = o.npcs.at(npc);
        if (!at || !mount) throw new Error(`story: no townsperson "${npc}" in sight`);
        const ny = o.npcs.yaw(npc);
        const x = at[0] + Math.sin(ny) * dist;
        const z = at[2] + Math.cos(ny) * dist;
        const yaw = ny + Math.PI;
        teleportHero(mount, x, z, yaw);
        const r = mount.rig();
        r.yawHeld = true;
        r.camera?.setYaw(yaw);
        r.control().cameraYaw = yaw;
      },
      problems: () => layerOrNull()?.problems() ?? [],
      /** The play-through bot (gate D1.3): start, run, report. */
      bot: {
        start: (phases?: string[]) => bot().start(phases ? { phases } : {}),
        run: (seconds: number) => bot().run(seconds),
        report: () => bot().report(),
      },
    },
  };
  let theBot: ReturnType<typeof storyBot> | null = null;
  const bot = (): ReturnType<typeof storyBot> => {
    if (!theBot) {
      if (!mount || !runtime || !dispatch) throw new Error("story: the bot needs the sim");
      theBot = storyBot({ m: mount, layer: need(), runtime, dispatch, scenes: () => scenes.playing(), npcs: o.npcs });
    }
    return theBot;
  };
  return self;
}

/** The story scene's mount. */
export type StoryMount = ReturnType<typeof storySceneFor>;
